import { ChatMessageAction, Message, PresenceMember } from "@ably/chat";
import {
  useChatClient,
  useMessages,
  usePresence,
  usePresenceListener,
} from "@ably/chat/react";
import { useUserData } from "../../../hooks/useUserData";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { ReactionTypeEntity } from "../../../services/gc/types/reactionTypeEntity";
import { MessageReaction, ModerationError } from "../types";
import { useToast } from "@chakra-ui/react";
import { useChannel } from "ably/react";
import { useHandleLogout } from "../../../hooks/useHandleLogout";
import { apiClient } from "../../../services/axiosInstance";
import { ServerResponse } from "../../../types";
import { publishChatJoinedEvent } from "../../../screens/root/api/eventsApi";

interface ModerateResponse {
  flagged: boolean;
  categories?: string[];
}

export function useChat(
  roomId: string,
  guests: string[] = [],
  guestIcon?: string,
) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [messagesBySerial, setMessagesBySerial] = useState<
    Record<string, Message>
  >({});
  const { userData } = useUserData();
  const chat = useChatClient();

  // Listen for users joining and leaving the room
  const { presenceData } = usePresenceListener();

  // Build members record from presenceData array.
  // Stabilize by keying on a sorted clientId string to avoid re-renders
  // when presenceData gets a new array reference with the same members.
  const memberIds = presenceData.map((m) => m.clientId).sort().join(",");
  const members = useMemo(() => {
    return presenceData.reduce(
      (acc: Record<string, PresenceMember>, member) => {
        acc[member.clientId] = member;
        return acc;
      },
      {},
    );
  }, [memberIds]);

  const userPresenceData = useMemo(
    () => ({
      username: userData?.name ?? "",
      isGold: userData?.gold ?? false,
      avatar: userData?.avatar_tiny_url ?? "",
      isAdmin: userData?.is_admin ?? false,
      isGuest: guests.includes(userData?.id + ""),
      guestIcon: guestIcon ?? "",
    }),
    [userData, guests, guestIcon],
  );

  const { leave: leavePresence } = usePresence({
    initialData: userPresenceData,
  });

  // Track chat join event
  useEffect(() => {
    publishChatJoinedEvent(roomId);
  }, [roomId]);

  // Global "broadcast" channel that allows the api server
  // to communicate with all chat clients accross channels
  const toast = useToast();
  const handleLogout = useHandleLogout();
  useChannel("gc:broadcast", (message) => {
    if (message.name === "message.banned") {
      if (message.data.clientId === chat?.clientId) {
        leavePresence();
        handleLogout();
        toast({
          title: "You have been banned.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  });

  // Listen for message events in the room and updates
  // the messages state and deletes messages when needed
  const {
    roomStatus,
    sendMessage: ablySendMessage,
    updateMessage: ablyUpdateMessage,
    deleteMessage: ablyDeleteMessage,
    history,
  } = useMessages({
    listener: (event) => {
      saveMessageBySerial(event.message);
    },
  });

  // Reset state when switching rooms so stale messages don't persist
  useLayoutEffect(() => {
    setMessagesBySerial({});
    setIsLoaded(false);
  }, [roomId]);

  // Get the historical messages in the room. Depending on `history` (stable
  // per-room via useCallback([context])) ensures we refetch when the room
  // context switches, even if roomStatus stays "attached".
  useLayoutEffect(() => {
    if (roomStatus !== "attached") return;

    let cancelled = false;

    history({ limit: 50 }).then((messages) => {
      if (cancelled) return;

      setMessagesBySerial(
        messages.items.reduce(
          (acc, curr) => {
            if (curr.action !== ChatMessageAction.MessageDelete)
              acc[curr.serial] = curr;
            return acc;
          },
          {} as Record<string, Message>,
        ),
      );
      setIsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [roomStatus, history]);

  useLayoutEffect(() => {
    if (roomStatus !== "attached") {
      setIsLoaded(false);
    }
  }, [roomStatus]);

  const saveMessageBySerial = (message: Message) => {
    setMessagesBySerial((prevMessages) => {
      if (message.action === ChatMessageAction.MessageDelete) {
        const { [message.serial]: _, ...rest } = prevMessages;
        return rest;
      }
      return {
        ...prevMessages,
        [message.serial]: message,
      };
    });
  };

  const handleDeleteMessage = async (message: Message) => {
    setMessagesBySerial((prev) => {
      const { [message.serial]: _, ...rest } = prev;
      return rest;
    });
    try {
      // First update the message with delete action in metadata
      // so mobile (Pub/Sub SDK) can identify it as a deletion
      await ablyUpdateMessage(message.serial, {
        text: message.text,
        metadata: {
          ...message.metadata,
          serial: message.serial,
          action: "message.delete",
        },
      });
      await ablyDeleteMessage(message.serial);
    } catch (error) {
      console.error("Failed to delete message", error);
      saveMessageBySerial(message);
    }
  };

  // Populates the reaction object by adding or removing
  // the currents user's reactions
  const handleToggleReaction = async (
    message: Message,
    reaction: ReactionTypeEntity,
  ) => {
    const reactions = {
      ...((message.metadata.reactions ?? {}) as Record<
        string,
        MessageReaction[]
      >),
    };

    if (!reactions[reaction.id]) reactions[reaction.id] = [];

    const reactionArray = [...reactions[reaction.id]];
    const index = reactionArray.findIndex((r) => r.clientId === chat?.clientId);
    if (index > -1) {
      reactionArray.splice(index, 1);
    } else {
      reactionArray.push({ clientId: chat?.clientId ?? "", reaction });
    }
    reactions[reaction.id] = reactionArray;

    const updatedMetadata = {
      ...message.metadata,
      reactions,
      action: "message.update",
    };
    saveMessageBySerial({ ...message, metadata: updatedMetadata } as Message);
    try {
      await ablyUpdateMessage(message.serial, {
        text: message.text,
        metadata: updatedMetadata,
      });
    } catch (error) {
      console.error("Failed to add reaction", error);
      saveMessageBySerial(message);
    }
  };

  const handleSendMessage = async (message: string) => {
    // Send message via Ably Chat SDK first (instant UX)
    const sentMessage = await ablySendMessage({
      text: message,
      metadata: {
        username: userData?.name ?? "",
        isGold: userData?.gold ?? false,
        avatar: userData?.avatar_tiny_url ?? "",
        isAdmin: userData?.is_admin ?? false,
        isGuest: guests.includes(userData?.id + ""),
        guestIcon: guestIcon ?? "",
      },
    });

    // Then check moderation - backend will delete if flagged
    try {
      const response = await apiClient.post<ServerResponse<ModerateResponse>>(
        "/v1/chat/moderate",
        {
          meta: {},
          payload: {
            message: message,
            room_id: roomId,
            serial: sentMessage.serial,
          },
        },
      );

      const result = response.data.payload.results;
      if (result?.flagged && result?.categories) {
        throw new ModerationError(result.categories);
      }
    } catch (error) {
      if (error instanceof ModerationError) {
        throw error;
      }
      // Don't throw on moderation API errors - message was already sent
      console.error("Failed to moderate message", error);
    }
  };

  return {
    isLoaded,
    messageArray: Object.values(messagesBySerial).sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    ),
    members,
    presenceMembers: presenceData.length,
    roomStatus,
    handleSendMessage,
    handleDeleteMessage,
    handleToggleReaction,
  };
}

export interface ChatState {
  isLoaded: boolean;
  messageArray: Message[];
  members: Record<string, PresenceMember>; // or a typed member object if you have one
  roomStatus: string; // or enum/string union for statuses

  handleDeleteMessage: (message: Message) => Promise<void>;
  handleToggleReaction: (
    message: Message,
    reaction: ReactionTypeEntity,
  ) => Promise<void>;
  handleSendMessage: (message: string) => Promise<void>;
}

export const ChatContext = createContext<ChatState | undefined>(undefined);

export const ChatProvider = ({
  children,
  roomId,
  guests,
}: {
  children: React.ReactNode;
  roomId: string;
  guests?: string[];
}) => {
  const chat = useChat(roomId, guests);
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
